import React, { useState, useRef } from 'react';
import { X, Camera, Crop } from 'lucide-react';
import Cropper from 'react-easy-crop';
import { Post } from '../../contexts/PostContext';
import { usePosts } from '../../contexts/PostContext';
import { supabase } from '../../lib/supabase';
import TopicSelect from './TopicSelect';
import { useAuth } from '../../contexts/AuthContext';

interface EditPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const EditPostModal: React.FC<EditPostModalProps> = ({ isOpen, onClose, post }) => {
  const { editPost, isLoading } = usePosts();
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [subject, setSubject] = useState(post.subject);
  const [content, setContent] = useState(post.content);
  const [photoURL, setPhotoURL] = useState(post.photo);
  const [topic, setTopic] = useState(post.topic || '');
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);

  const onCropComplete = (croppedArea: any, croppedAreaPixels: CropArea) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: CropArea
  ): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas is empty'));
        }
      }, 'image/jpeg', 0.95);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError('');
    
    // Create a temporary URL for the image
    const objectUrl = URL.createObjectURL(file);
    setPhotoURL(objectUrl);
    setIsCropping(true);
  };

  const handleCropSave = async () => {
    if (!photoURL || !croppedAreaPixels || !currentUser) return;

    setIsUploading(true);
    try {
      const croppedImage = await getCroppedImg(photoURL, croppedAreaPixels);
      
      // Upload directly to user's folder
      const fileName = `${currentUser.id}/${crypto.randomUUID()}.jpg`;

      // Upload file to Supabase Storage
      const { error: uploadError, data } = await supabase.storage
        .from('posts')
        .upload(fileName, croppedImage, {
          contentType: 'image/jpeg',
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('posts')
        .getPublicUrl(fileName);

      setPhotoURL(publicUrl);
      setIsCropping(false);

      // Clean up the temporary object URL
      URL.revokeObjectURL(photoURL);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim() || !content.trim() || !photoURL.trim() || !topic.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      await editPost(post.id, {
        subject: subject.trim(),
        content: content.trim(),
        photo: photoURL.trim(),
        topic: topic.trim()
      });
      
      onClose();
    } catch (error) {
      console.error('Error updating post:', error);
      setError('Failed to update post. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative bg-[#272727] rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#272727] p-6 border-b border-[#404040] flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Edit Post</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-400 mb-2">
                Post Title
              </label>
              <input
                type="text"
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#404040] hover:border-[#505050] transition-colors"
                maxLength={100}
              />
            </div>

            <div>
              <TopicSelect value={topic} onChange={setTopic} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Cover Image
              </label>
              
              <div className="relative aspect-video rounded-lg overflow-hidden bg-[#272727] border-2 border-dashed border-[#404040] group">
                {isCropping ? (
                  <div className="relative h-[400px]">
                    <Cropper
                      image={photoURL}
                      crop={crop}
                      zoom={zoom}
                      aspect={16/9}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center space-x-4">
                      <input
                        type="range"
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        className="w-32"
                      />
                      <button
                        type="button"
                        onClick={handleCropSave}
                        disabled={isUploading}
                        className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors flex items-center space-x-2 disabled:bg-purple-400/50 disabled:cursor-not-allowed"
                      >
                        {isUploading ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        ) : (
                          <>
                            <Crop className="h-5 w-5" />
                            <span>Crop Image</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <img 
                      src={photoURL} 
                      alt="Cover"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {isUploading ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                      ) : (
                        <Camera className="h-8 w-8 text-white" />
                      )}
                    </button>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>

            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-400 mb-2">
                Post Content
              </label>
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={6}
                className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#404040] hover:border-[#505050] transition-colors resize-none"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || isUploading}
              className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:bg-purple-400/50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPostModal;